// pages/admin/AdminUnits.js
// ניהול יחידות — CRUD
//
// API:
//   GET /units → רשימה
//   POST /units { name, description } → יצירה
//   PUT /units/{id} { name, description } → עדכון
//   DELETE /units/{id} → מחיקה (hard delete)
//
// UI: DataTable + Add button + Edit dialog + Delete confirm

import { useState, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Snackbar,
  Alert,
  IconButton,
  LinearProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/EditOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import CloseIcon from '@mui/icons-material/Close';
import BusinessIcon from '@mui/icons-material/BusinessOutlined';
import { useTranslation } from 'react-i18next';

import { DataTable, ConfirmDialog } from '../../components/shared';
import { useApi, useApiMutation } from '../../hooks/useApi';

// =============================================================================
// AdminUnits Page
// =============================================================================
export default function AdminUnits() {
  const { t } = useTranslation();

  // API — fetch units list
  const { data: units, loading, error, refetch } = useApi('/units', {
    defaultData: [],
  });

  // Mutation hook
  const { mutate, loading: mutating } = useApiMutation();

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUnit, setEditingUnit] = useState(null); // null = add, object = edit
  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formError, setFormError] = useState('');

  // Delete confirm state
  const [deleteUnit, setDeleteUnit] = useState(null);

  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // ===================================================================
  // Dialog handlers
  // ===================================================================
  const handleOpenAdd = useCallback(() => {
    setEditingUnit(null);
    setFormName('');
    setFormDescription('');
    setFormError('');
    setDialogOpen(true);
  }, []);

  const handleOpenEdit = useCallback((unit) => {
    setEditingUnit(unit);
    setFormName(unit.name || '');
    setFormDescription(unit.description || '');
    setFormError('');
    setDialogOpen(true);
  }, []);

  const handleCloseDialog = useCallback(() => {
    if (mutating) return;
    setDialogOpen(false);
    setEditingUnit(null);
  }, [mutating]);

  // ===================================================================
  // Save (create or update)
  // ===================================================================
  const handleSave = useCallback(async () => {
    // Validation
    if (mutating) return;
    const trimmedName = formName.trim();
    if (!trimmedName) {
      setFormError(t('common.required'));
      return;
    }

    setFormError('');
    const body = {
      name: trimmedName,
      description: formDescription.trim() || null,
    };

    let result;
    if (editingUnit) {
      result = await mutate('put', `/units/${editingUnit.id}`, body);
    } else {
      result = await mutate('post', '/units', body);
    }

    if (result.success) {
      setDialogOpen(false);
      setEditingUnit(null);
      refetch();
      setSnackbar({
        open: true,
        message: result.message || t('common.success'),
        severity: 'success',
      });
    } else {
      setFormError(result.error || t('common.error'));
    }
  }, [formName, formDescription, editingUnit, mutate, mutating, refetch, t]);

  // ===================================================================
  // Delete
  // ===================================================================
  const handleDelete = useCallback(async () => {
    if (!deleteUnit || mutating) return;

    const result = await mutate('delete', `/units/${deleteUnit.id}`);

    if (result.success) {
      setDeleteUnit(null);
      refetch();
      setSnackbar({
        open: true,
        message: result.message || t('common.success'),
        severity: 'success',
      });
    } else {
      setDeleteUnit(null);
      setSnackbar({
        open: true,
        message: result.error || t('common.error'),
        severity: 'error',
      });
    }
  }, [deleteUnit, mutating, mutate, refetch, t]);

  // ===================================================================
  // Table config
  // ===================================================================
  const columns = useMemo(() => [
    {
      key: 'name',
      label: t('units.unitName'),
      sortable: true,
    },
    {
      key: 'description',
      label: t('units.unitDescription'),
      sortable: false,
      render: (val) => val || '—',
    },
    {
      key: 'active',
      label: t('common.status'),
      sortable: true,
      width: 100,
      render: (val) => (
        <Chip
          label={val ? t('common.active') : t('common.inactive')}
          color={val ? 'success' : 'default'}
          size="small"
          variant="outlined"
        />
      ),
    },
  ], [t]);

  const actions = useMemo(() => [
    {
      key: 'edit',
      icon: <EditIcon fontSize="small" />,
      label: t('common.edit'),
      onClick: handleOpenEdit,
    },
    {
      key: 'delete',
      icon: <DeleteIcon fontSize="small" />,
      label: t('common.delete'),
      color: 'error',
      onClick: (unit) => setDeleteUnit(unit),
    },
  ], [t, handleOpenEdit]);

  // Toolbar — add button
  const toolbar = useMemo(() => (
    <Button
      data-testid="units__button--add"
      variant="contained"
      startIcon={<AddIcon />}
      onClick={handleOpenAdd}
      size="small"
    >
      {t('units.addUnit')}
    </Button>
  ), [t, handleOpenAdd]);

  // ===================================================================
  // Render
  // ===================================================================
  return (
    <Box data-testid="units__page" sx={{ pb: 4 }}>
      {/* Page title */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <BusinessIcon color="primary" />
        <Typography variant="h5" fontWeight={600}>
          {t('units.title')}
        </Typography>
      </Box>

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      )}

      {/* Table */}
      <DataTable
        testId="units__table"
        columns={columns}
        rows={units || []}
        actions={actions}
        loading={loading}
        searchable
        toolbar={toolbar}
        defaultSortKey="name"
        emptyStateProps={{
          title: t('units.noUnits'),
          message: t('units.noUnitsMessage'),
          actionLabel: t('units.addUnit'),
          onAction: handleOpenAdd,
        }}
      />

      {/* ============================================================= */}
      {/* Add/Edit Dialog                                                */}
      {/* ============================================================= */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        data-testid="units__dialog--edit"
      >
        {mutating && <LinearProgress />}

        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {editingUnit ? t('units.editUnit') : t('units.addUnit')}
          <IconButton aria-label={t('common.close')} size="small" onClick={handleCloseDialog} disabled={mutating}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              data-testid="units__dialog--edit__input--name"
              label={t('units.unitName')}
              value={formName}
              onChange={(e) => {
                setFormName(e.target.value);
                setFormError('');
              }}
              onKeyDown={(e) => e.key === 'Enter' && handleSave()}
              fullWidth
              autoFocus
              required
              disabled={mutating}
              error={!!formError}
              helperText={formError}
            />

            <TextField
              data-testid="units__dialog--edit__input--description"
              label={t('units.unitDescription')}
              value={formDescription}
              onChange={(e) => setFormDescription(e.target.value)}
              fullWidth
              multiline
              rows={3}
              disabled={mutating}
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button data-testid="units__dialog--edit__button--cancel" onClick={handleCloseDialog} disabled={mutating}>
            {t('common.cancel')}
          </Button>
          <Button
            data-testid="units__dialog--edit__button--save"
            variant="contained"
            onClick={handleSave}
            disabled={mutating || !formName.trim()}
          >
            {mutating ? t('common.saving') : t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ============================================================= */}
      {/* Delete Confirm Dialog                                          */}
      {/* ============================================================= */}
      <ConfirmDialog
        open={!!deleteUnit}
        title={t('units.deleteUnit')}
        message={t('units.deleteConfirm', { name: deleteUnit?.name })}
        onConfirm={handleDelete}
        onCancel={() => setDeleteUnit(null)}
        confirmLabel={t('common.delete')}
        destructive
        loading={mutating}
      />

      {/* ============================================================= */}
      {/* Snackbar                                                       */}
      {/* ============================================================= */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
